<template>
<div class="onboardFormVue">
    <!-- Client logo header (matches universal pages: brand at top) -->
    <header class="onboard-brand-header">
        <img
            v-if="client && client.logo"
            :src="client.logo"
            :alt="client.name || 'Client logo'"
            class="onboard-brand-logo"
        />
        <span v-else-if="client && client.name" class="onboard-brand-name">{{ client.name }}</span>
    </header>

    <main class="onboard-main">
        <div class="onboard-card">

            <!-- Title / profile description -->
            <div class="onboard-header">
                <h1 class="onboard-title">Welcome</h1>
                <p v-if="profile.description" class="onboard-subtitle">{{ profile.description }}</p>
            </div>

            <!-- Onboarding note from the profile -->
            <div v-if="profile.onboarding_note" class="onboard-note">
                {{ profile.onboarding_note }}
            </div>

            <!-- #1459 — email-verify landing (one-click confirm) -->
            <div v-if="mode === 'verify'" class="onboard-success">
                <template v-if="verifyState === 'working'">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <h2>Confirming…</h2>
                    <p>One moment while we confirm your email.</p>
                </template>
                <template v-else-if="verifyState === 'done'">
                    <i class="fa fa-check-circle" aria-hidden="true"></i>
                    <h2>You're all set</h2>
                    <p>Your email is confirmed and you're now on the dispatch list. Watch your phone and email for a password setup link.</p>
                </template>
                <template v-else>
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <h2>Link expired</h2>
                    <p>This confirmation link is invalid or has expired. Please start onboarding again, or contact your steward.</p>
                </template>
            </div>

            <!-- Already-onboarded short-circuit -->
            <div v-else-if="alreadyOnboarded" class="onboard-success">
                <i class="fa fa-check-circle" aria-hidden="true"></i>
                <h2>You're all set</h2>
                <p>This onboarding link has already been completed. If you need to sign in, check your phone or email for the password setup link, or contact your steward.</p>
            </div>

            <!-- Submitted success (classic token flow) -->
            <div v-else-if="submitted" class="onboard-success">
                <i class="fa fa-paper-plane" aria-hidden="true"></i>
                <h2>Profile submitted</h2>
                <p v-if="form.cell_display || form.cell">We just sent a password setup link to <strong>{{ form.email }}</strong> and to your cell <strong>{{ form.cell_display || form.cell }}</strong>. Tap either link to set your password and finish.</p>
                <p v-else>We just sent a password setup link to <strong>{{ form.email }}</strong>. Tap the link to set your password and finish.</p>
            </div>

            <!-- #1459 — one-form success: confirm-your-email -->
            <div v-else-if="verifyEmailSent" class="onboard-success">
                <i class="fa fa-envelope" aria-hidden="true"></i>
                <h2>Check your email</h2>
                <p>We sent a confirmation link to <strong>{{ form.email }}</strong>. Tap it to finish — you'll be added to the dispatch list right away.</p>
            </div>

            <!-- #1459 — invalid/expired short code: styled card instead of a bare 404 -->
            <!-- #1750 — two different failures, two different messages. Telling a
                 worker a never-resolvable link "expired" invites them to refresh,
                 which cannot possibly help, and hides the real fault from the
                 steward who could actually fix it. -->
            <div v-else-if="loadFailed" class="onboard-success" data-testid="onboard-load-failed">
                <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                <template v-if="loadFailReason === 'tenant_not_found'">
                    <h2 data-testid="onboard-fail-tenant">This link is not valid for this address</h2>
                    <p>
                        This onboarding link was created with the wrong web address, so it cannot
                        be opened here. It is not expired — refreshing will not help. Please ask
                        your steward to send you a new link or QR code.
                    </p>
                </template>
                <!-- #1827 — a GATED link is not an expired one. The code is real
                     and the steward's QR is fine; sign-ups are switched off, out
                     of their date window, or done for today. Saying "expired"
                     here would send the worker back for a new QR that behaves
                     identically, and the steward hunting a fault that isn't one. -->
                <template v-else-if="loadFailReason === 'qr_daily_cap_reached'">
                    <h2 data-testid="onboard-fail-cap">That's everyone for today</h2>
                    <p>This sign-up link has reached its limit for today. Please try again tomorrow, or ask your steward to sign you up directly.</p>
                </template>
                <template v-else-if="loadFailReason === 'qr_disabled' || loadFailReason === 'qr_window_closed'">
                    <h2 data-testid="onboard-fail-gated">Sign-ups are closed</h2>
                    <p>This link is not accepting new sign-ups right now. Please check with your steward.</p>
                </template>
                <!-- #1827 follow-up — throttled, not broken. Say "wait", not "expired". -->
                <template v-else-if="loadFailReason === 'too_many_requests'">
                    <h2 data-testid="onboard-fail-throttled">Too many attempts</h2>
                    <p>There have been too many attempts from your connection. Please wait a minute and reload this page — your link is still good.</p>
                </template>
                <template v-else>
                    <h2 data-testid="onboard-fail-expired">Link no longer active</h2>
                    <p>This onboarding link has expired or been refreshed. Please ask your steward for the current link or QR code.</p>
                </template>
            </div>

            <!-- Form -->
            <form v-else @submit.prevent="submit" novalidate>

                <!-- Honeypot — hidden from real users; bots that fill it are rejected -->
                <div class="onboard-hp" aria-hidden="true">
                    <label>Company URL</label>
                    <input type="text" tabindex="-1" autocomplete="off" v-model="companyUrl" />
                </div>

                <!-- Cell: editable in one-form (code) mode; read-only when prefilled from an SMS token -->
                <div class="form-group">
                    <label>Cell phone <span v-if="cellRequired" class="onboard-req">*</span><span v-else class="onboard-optional">(optional)</span></label>
                    <input
                        v-if="cellEditable"
                        type="tel"
                        autocomplete="tel"
                        inputmode="tel"
                        class="form-control onboard-input"
                        :class="{ 'is-invalid': errors.cell }"
                        v-model.trim="form.cell"
                        @blur="validateCell"
                        placeholder="(555) 123-4567"
                    />
                    <input v-else type="tel" class="form-control onboard-input onboard-input--readonly" :value="form.cell_display" readonly />
                    <small v-if="errors.cell" class="onboard-error">{{ errors.cell }}</small>
                    <small v-else class="onboard-help">We'll send your password setup link here.</small>
                </div>

                <!-- #1827 — SMS possession check. Only rendered when the profile
                     sets qr_require_sms_verify; the server enforces it either
                     way, so hiding this block can never be a way in. -->
                <div v-if="needsSmsCode" class="form-group" data-testid="onboard-sms-verify">
                    <label>Verification code <span class="onboard-req">*</span></label>
                    <div class="onboard-sms-row">
                        <input
                            type="text"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            maxlength="6"
                            class="form-control onboard-input"
                            :class="{ 'is-invalid': errors.sms_code }"
                            v-model.trim="form.sms_code"
                            placeholder="123456"
                            data-testid="onboard-sms-code"
                        />
                        <button
                            type="button"
                            class="btn btn-default onboard-sms-send"
                            :disabled="smsSending || !form.cell"
                            @click="sendSmsCode"
                            data-testid="onboard-sms-send"
                        >{{ smsSent ? 'Resend' : 'Text me a code' }}</button>
                    </div>
                    <small v-if="errors.sms_code" class="onboard-error">{{ errors.sms_code }}</small>
                    <small v-else-if="smsSent" class="onboard-help">We texted a 6-digit code to your cell. It expires in 10 minutes.</small>
                    <small v-else class="onboard-help">Enter your cell above, then tap "Text me a code".</small>
                </div>

                <!-- First name -->
                <div class="form-group">
                    <label>First name <span class="onboard-req">*</span></label>
                    <input
                        type="text"
                        autocomplete="given-name"
                        autocapitalize="words"
                        spellcheck="false"
                        class="form-control onboard-input"
                        v-model.trim="form.first_name"
                        :class="{ 'is-invalid': errors.first_name }"
                        @blur="validateField('first_name')"
                    />
                    <small v-if="errors.first_name" class="onboard-error">{{ errors.first_name }}</small>
                </div>

                <!-- Last name -->
                <div class="form-group">
                    <label>Last name <span class="onboard-req">*</span></label>
                    <input
                        type="text"
                        autocomplete="family-name"
                        autocapitalize="words"
                        spellcheck="false"
                        class="form-control onboard-input"
                        v-model.trim="form.last_name"
                        :class="{ 'is-invalid': errors.last_name }"
                        @blur="validateField('last_name')"
                    />
                    <small v-if="errors.last_name" class="onboard-error">{{ errors.last_name }}</small>
                </div>

                <!-- Email -->
                <div class="form-group">
                    <label>Email <span class="onboard-req">*</span></label>
                    <input
                        ref="emailInput"
                        type="email"
                        autocomplete="email"
                        autocapitalize="off"
                        spellcheck="false"
                        inputmode="email"
                        class="form-control onboard-input"
                        v-model.trim="form.email"
                        :class="{ 'is-invalid': errors.email }"
                        @blur="checkEmailExists()"
                    />
                    <small v-if="errors.email" class="onboard-error">{{ errors.email }}</small>
                    <small v-else-if="emailStatus === 'exists'" class="onboard-warn">
                        That email is already in our system — we'll link this onboarding to that account.
                    </small>
                    <small v-else-if="emailStatus === 'available'" class="onboard-help">
                        <i class="fa fa-check"></i> Email looks good.
                    </small>
                </div>

                <!-- Photo (shown only when the profile asks for one) -->
                <div v-if="photoMode !== 'off'">
                    <h3 class="onboard-section-h">
                        Photo
                        <span v-if="photoMode === 'required'" class="onboard-req">*</span>
                        <span v-else class="onboard-optional">(optional)</span>
                    </h3>
                    <p v-if="profile.photo_instructions" class="onboard-help onboard-photo-instructions">
                        {{ profile.photo_instructions }}
                    </p>
                    <div class="form-group">
                        <div v-if="photoPreview" class="onboard-photo-preview">
                            <img :src="photoPreview" alt="Your photo" />
                        </div>
                        <label class="onboard-photo-btn">
                            <i class="fa fa-camera" aria-hidden="true"></i>
                            <span>{{ photoPreview ? 'Retake or choose another' : 'Take or choose a photo' }}</span>
                            <input
                                type="file"
                                accept="image/*"
                                class="onboard-photo-input"
                                @change="handlePhoto"
                            />
                        </label>
                        <small v-if="photoUploading" class="onboard-help">
                            <i class="fa fa-spinner fa-spin"></i> Uploading photo&hellip;
                        </small>
                        <small v-else-if="photoUploaded" class="onboard-help">
                            <i class="fa fa-check"></i> Photo uploaded.
                        </small>
                        <small v-if="photoError" class="onboard-error">{{ photoError }}</small>
                    </div>
                </div>

                <!-- Address (optional) -->
                <h3 class="onboard-section-h">Address <span class="onboard-optional">(optional)</span></h3>

                <div class="form-group">
                    <label>Street address</label>
                    <input
                        type="text"
                        autocomplete="street-address"
                        autocapitalize="words"
                        class="form-control onboard-input"
                        v-model.trim="form.street"
                    />
                </div>

                <div class="form-group">
                    <label>PO Box</label>
                    <input
                        type="text"
                        autocomplete="off"
                        class="form-control onboard-input"
                        v-model.trim="form.pobox"
                    />
                </div>

                <div class="form-group">
                    <label>City</label>
                    <input
                        type="text"
                        autocomplete="address-level2"
                        autocapitalize="words"
                        class="form-control onboard-input"
                        v-model.trim="form.city"
                    />
                </div>

                <div class="onboard-row">
                    <div class="form-group onboard-col-2">
                        <label>State / Province</label>
                        <input
                            type="text"
                            autocomplete="address-level1"
                            autocapitalize="characters"
                            class="form-control onboard-input"
                            v-model.trim="form.state"
                        />
                    </div>
                    <div class="form-group onboard-col-2">
                        <label>Postal code</label>
                        <input
                            type="text"
                            autocomplete="postal-code"
                            inputmode="text"
                            class="form-control onboard-input"
                            v-model.trim="form.postal_code"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <label>Country</label>
                    <input
                        type="text"
                        autocomplete="country-name"
                        autocapitalize="words"
                        class="form-control onboard-input"
                        v-model.trim="form.country"
                        placeholder="United States"
                    />
                </div>

                <!-- Emergency contact card (optional) -->
                <h3 class="onboard-section-h">Emergency contact <span class="onboard-optional">(optional)</span></h3>

                <div class="form-group">
                    <label>Contact name</label>
                    <input
                        type="text"
                        autocomplete="off"
                        autocapitalize="words"
                        class="form-control onboard-input"
                        v-model.trim="form.emergency_name"
                    />
                </div>
                <div class="form-group">
                    <label>Relationship</label>
                    <input
                        type="text"
                        autocomplete="off"
                        autocapitalize="words"
                        class="form-control onboard-input"
                        v-model.trim="form.emergency_relationship"
                        placeholder="Spouse / Parent / Friend"
                    />
                </div>
                <div class="form-group">
                    <label>Contact phone</label>
                    <input
                        type="tel"
                        autocomplete="tel"
                        inputmode="tel"
                        class="form-control onboard-input"
                        v-model.trim="form.emergency_phone"
                    />
                </div>

                <!-- Submit -->
                <button
                    type="submit"
                    class="onboard-submit"
                    :disabled="submitting || !canSubmit"
                >
                    <i v-if="submitting" class="fa fa-spinner fa-spin"></i>
                    <span v-else>Submit &amp; Send Password Link</span>
                </button>

                <p v-if="submitError" class="onboard-error onboard-error--global">
                    {{ submitError }}
                </p>
            </form>

        </div>
    </main>

    <!-- Powered-by-CallSteward footer -->
    <footer class="onboard-footer">
        <a href="https://www.callsteward.com" target="_blank" rel="noopener">
            <img src="/static/assets/logo.png" alt="CallSteward" class="onboard-footer-logo" />
        </a>
    </footer>

    <!-- #1459 — email-conflict modal. Opens over the dimmed form on both submit
         paths when the email is already in this local (email_in_client) or
         registered elsewhere (email_in_database). -->
    <div v-if="emailConflict" class="onboard-modal-backdrop" @click.self="closeEmailConflict()">
        <div class="onboard-modal" role="dialog" aria-modal="true" aria-labelledby="emailConflictTitle">
            <h2 id="emailConflictTitle" class="onboard-modal-title">{{ emailConflictCopy.title }}</h2>
            <p class="onboard-modal-body">{{ emailConflictCopy.body }}</p>
            <div class="onboard-modal-actions">
                <button type="button" class="onboard-modal-btn onboard-modal-btn--primary" @click="emailConflictSignIn()">
                    {{ emailConflictCopy.cta }}
                </button>
                <button type="button" class="onboard-modal-btn onboard-modal-btn--ghost" @click="closeEmailConflict()">
                    Close
                </button>
            </div>
        </div>
    </div>

    <!-- #1720 P1 — "Is this location correct?" pin-confirm overlay. Reuses the
         #1715 VenuePinMap; seeds from the typed address, lets the worker confirm
         or drag the pin, then continues the submit with verified home coords. -->
    <div v-if="showVerify" class="onboard-modal-backdrop" @click.self="closeMap()">
        <div class="onboard-modal" role="dialog" aria-modal="true" aria-labelledby="pinConfirmTitle" style="max-width:560px;">
            <h2 id="pinConfirmTitle" class="onboard-modal-title">Is this location correct?</h2>
            <p class="onboard-modal-body">Confirm your home location, or tap “Move pin” to drag it to the right spot.</p>
            <VenuePinMap
                v-if="showVerify"
                :address="fullAddress"
                :draggable="pinDraggable"
                :zoom="17"
                @update:coords="onCoords" />
            <div class="onboard-modal-actions">
                <button type="button" class="onboard-modal-btn onboard-modal-btn--ghost" @click="closeMap()">
                    Cancel
                </button>
                <template v-if="!pinDraggable">
                    <button type="button" class="onboard-modal-btn onboard-modal-btn--primary" @click="confirmPin()">
                        That's correct
                    </button>
                    <button type="button" class="onboard-modal-btn onboard-modal-btn--ghost" @click="enableMovePin()">
                        Move pin
                    </button>
                </template>
                <button v-else type="button" class="onboard-modal-btn onboard-modal-btn--primary" @click="confirmPin()">
                    Save this location
                </button>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import service from '../services.js';
import VenuePinMap from './venue/VenuePinMap.vue'; // #1720 P1 — reuse the #1715 pin editor

export default {
    components: { VenuePinMap },
    data() {
        return {
            // Loaded from /api/v3/onboarding/intake/{token} — returns
            // { profile: {description, onboarding_note}, cell, cell_display,
            //   already_completed }.
            profile: { description: '', onboarding_note: '' },
            client: null,           // { name, logo } — surfaced for the brand header
            alreadyOnboarded: false,

            form: {
                first_name: '',
                last_name: '',
                email: '',
                cell: '',
                cell_display: '',
                sms_code: '',   // #1827 — QR possession check

                street: '',
                pobox: '',
                city: '',
                state: '',
                postal_code: '',
                country: '',
                // #1720 P1 — home coordinate, set by the "Is this location correct?" pin step.
                lat: null,
                lng: null,
                coords_source: null,
                coords_verified: false,
                emergency_name: '',
                emergency_relationship: '',
                emergency_phone: '',
            },
            // #1720 P1 — pin-confirm overlay state.
            showVerify: false,
            pinDraggable: false,
            errors: {},
            emailStatus: '', // 'available' | 'exists' | ''
            submitting: false,
            submitted: false,
            submitError: '',
            // #1459 — email-conflict modal state. null = closed; otherwise the
            // backend's status_message: 'email_in_client' | 'email_in_database'.
            emailConflict: null,
            // #1459 — tenant subdomain echoed by the backend; named in the
            // cross-client (email_in_database) "new association" modal copy.
            emailConflictSubdomain: '',

            // #1459 one-form (code) mode + email-verify mode
            nonce: '',              // signed "form loaded at" for the time-trap
            companyUrl: '',         // honeypot — must stay empty (hidden field)
            verifyEmailSent: false, // code-mode success: "check your email"
            verifyState: 'working', // verify mode: 'working' | 'done' | 'failed'
            loadFailed: false,      // invalid/expired short code -> styled card
            // #1750 — which failure, so the card can say something true:
            // 'invalid_or_expired' (rotated/deactivated) | 'tenant_not_found'
            // (minted on a non-tenant host — a broken link, not an old one).
            loadFailReason: 'invalid_or_expired',

            // #1827 — SMS possession check (profile.qr_require_sms_verify).
            requiresSmsVerify: false,
            smsSending: false,
            smsSent: false,

            // Photo capture (#1482 task 11)
            photoPreview: '',
            photoUploading: false,
            photoUploaded: false,
            photoError: '',
        };
    },
    computed: {
        // 'verify' = email-confirm landing; 'code' = one-form self-service
        // (collects phone); 'token' = classic per-stub SMS link.
        mode() {
            if (this.$route.params.vtoken) return 'verify';
            if (this.$route.params.code) return 'code';
            return 'token';
        },
        isCodeMode() {
            return this.mode === 'code';
        },
        // #1827 — the code step belongs to the public QR flow only. A dispatched
        // token link is already a possession proof (it was texted to that phone),
        // so re-verifying there would be asking the same question twice.
        needsSmsCode() {
            return this.isCodeMode && this.requiresSmsVerify;
        },
        // #1720 P1 — the address is optional; only gate on the pin step when the
        // worker actually entered something worth locating.
        hasEnteredAddress() {
            return !!(this.form.street || this.form.city || this.form.state
                || this.form.postal_code || this.form.country);
        },
        // #1720 P1 — the address string the map geocodes to seed the pin.
        fullAddress() {
            return [this.form.street, this.form.city, this.form.state,
                this.form.postal_code, this.form.country].filter(Boolean).join(', ');
        },
        // The cell field is ALWAYS editable on the intake form (token + code
        // modes). The worker can add a cell on an email-only link, or change the
        // number an SMS link was sent to — never lock it read-only.
        cellEditable() {
            return this.mode !== 'verify';
        },
        // Cell is REQUIRED only in the one-form (code) self-service flow, where
        // it's the primary contact. On a dispatched token link (email/SMS) the
        // email is the required login identity and the cell is an optional
        // second channel — never force the worker to supply one.
        cellRequired() {
            return this.isCodeMode;
        },
        photoMode() {
            return this.profile.photo_mode || 'off';
        },
        canSubmit() {
            return !!this.form.first_name
                && !!this.form.last_name
                && !!this.form.email
                && !this.errors.first_name
                && !this.errors.last_name
                && !this.errors.email
                && (!this.cellRequired || !!this.form.cell)
                && !this.errors.cell
                && !this.photoUploading
                && (this.photoMode !== 'required' || this.photoUploaded);
        },
        // #1459 — copy for the two email-conflict modal states. Titled the same;
        // body differs (already in THIS local vs. registered elsewhere).
        emailConflictCopy() {
            if (this.emailConflict === 'email_in_client') {
                return {
                    title: 'Email already in use',
                    body: "You're already set up with this local. Please sign in to continue — or contact your steward if you need help.",
                    cta: 'Sign in',
                };
            }
            if (this.emailConflict === 'email_in_database') {
                var local = this.emailConflictSubdomain || 'this local';
                return {
                    title: 'You already have a CallSteward account',
                    body: 'A new association to ' + local + ' is being added to your CallSteward account. Please log in to continue.',
                    cta: 'Log in',
                };
            }
            return { title: '', body: '', cta: 'Sign in' };
        },
    },
    methods: {
        loadIntake() {
            const token = this.$route.params.token;
            if (!token) return;
            service.getUrl('/api/v3/onboarding/intake/' + token, {})
                .then((res) => {
                    const r = res.data && res.data.result;
                    if (!r) return;
                    this.profile = r.profile || {};
                    this.client = r.client || null;
                    this.form.cell = r.cell || '';
                    this.form.cell_display = r.cell_display || r.cell || '';
                    // Pre-fill the email the dispatcher entered when sending the
                    // link, so the worker doesn't have to re-type it.
                    if (r.email) this.form.email = r.email;
                    this.alreadyOnboarded = !!r.already_completed;
                })
                .catch(() => {
                    this.submitError = 'This onboarding link is invalid or expired.';
                });
        },
        // #1459 — one-form (code) load: tenant branding + signed time nonce.
        // No cell is pre-filled; the worker types it.
        loadByCode() {
            const code = this.$route.params.code;
            if (!code) return;
            service.getUrl('/api/v3/onboarding/intake/code/' + code, {})
                .then((res) => {
                    const r = res.data && res.data.result;
                    if (!r) return;
                    this.profile = r.profile || {};
                    this.client = r.client || null;
                    this.nonce = r.nonce || '';
                    this.requiresSmsVerify = !!(r.profile && r.profile.requires_sms_verify);
                })
                .catch((e) => {
                    // #1750 — do NOT collapse these. A 404 `tenant_not_found`
                    // means the link was minted against a host that cannot
                    // resolve a tenant (login./admin.*) and is dead no matter
                    // how fresh it is; a 410 `invalid_or_expired` means the code
                    // was genuinely rotated or deactivated. Reporting the first
                    // as "expired" sent FS #16433 at the TTL subsystem for two
                    // days, and told the worker to do the one thing that cannot
                    // help — refresh.
                    const status = e && e.response ? e.response.status : 0;
                    const reason = (e && e.response && e.response.data && e.response.data.status_message) || '';
                    this.loadFailed = true;
                    // #1827 — the gate reasons are pass-through for the same
                    // reason #1750 split the two above: each one points the
                    // worker at a different (and actually useful) next step.
                    if (['qr_disabled', 'qr_window_closed', 'qr_daily_cap_reached'].indexOf(reason) !== -1) {
                        this.loadFailReason = reason;
                    } else if (reason === 'too_many_requests' || status === 429) {
                        // #1827 follow-up — a throttled load is a RETRYABLE
                        // condition. Falling through to 'invalid_or_expired'
                        // would tell the worker their good link is dead and send
                        // them to a steward for a replacement that behaves the
                        // same — the #1750 mistake in a new coat.
                        this.loadFailReason = 'too_many_requests';
                    } else {
                        this.loadFailReason = (status === 404 || reason === 'tenant_not_found')
                            ? 'tenant_not_found'
                            : 'invalid_or_expired';
                    }
                });
        },
        // #1827 — ask the server to text a 6-digit code to the number typed in
        // the cell field. Errors are reported against the field the worker can
        // actually act on (cell for a bad number, sms_code otherwise).
        sendSmsCode() {
            const code = this.$route.params.code;
            if (!code || !this.form.cell) return;
            this.smsSending = true;
            this.$delete(this.errors, 'sms_code');
            service.postUrl('/api/v3/onboarding/intake/code/' + code + '/sms', { phone: this.form.cell }, {})
                .then(() => { this.smsSent = true; })
                .catch((err) => {
                    const msg = err && err.response && err.response.data && err.response.data.status_message;
                    const status = err && err.response ? err.response.status : 0;
                    if (msg === 'invalid_phone') {
                        this.$set(this.errors, 'cell', 'Please enter a valid mobile number.');
                    } else if (msg === 'too_many_requests' || status === 429) {
                        // #1827 follow-up — the throttle now answers in the same
                        // refusal shape as every other gate, so this reads the
                        // message like the rest. The bare 429 stays as a fallback
                        // for the group-level `throttle:30,1`, which is stock
                        // Laravel and still replies {"message":"Too Many Attempts."}.
                        this.$set(this.errors, 'sms_code', 'Too many code requests. Please wait a few minutes and try again.');
                    } else {
                        this.$set(this.errors, 'sms_code', 'We could not send a code just now. Please try again, or contact your steward.');
                    }
                })
                .finally(() => { this.smsSending = false; });
        },
        // #1459 — email-verify landing: one-click promote stub → basic.
        runVerify() {
            const vtoken = this.$route.params.vtoken;
            if (!vtoken) { this.verifyState = 'failed'; return; }
            service.getUrl('/api/v3/onboarding/intake/' + vtoken + '/verify', {})
                .then((res) => {
                    const ok = res.data && res.data.status_code === 1;
                    this.verifyState = ok ? 'done' : 'failed';
                })
                .catch(() => { this.verifyState = 'failed'; });
        },
        validateField(field) {
            const v = this.form[field] || '';
            if (!v) {
                this.$set(this.errors, field, 'Required');
            } else {
                this.$delete(this.errors, field);
            }
        },
        validateCell() {
            const v = (this.form.cell || '').replace(/[^0-9]/g, '');
            // Empty is fine unless the cell is required (one-form flow). On a
            // dispatched link the cell is optional — don't flag a blank field.
            if (!v) {
                if (this.cellRequired) {
                    this.$set(this.errors, 'cell', 'Required');
                } else {
                    this.$delete(this.errors, 'cell');
                }
                return;
            }
            if (v.length < 10) {
                this.$set(this.errors, 'cell', 'Enter a valid mobile number');
            } else {
                this.$delete(this.errors, 'cell');
            }
        },
        checkEmailExists() {
            const filter = /^([\w\-\.]+)@(([\w\-]+\.)+)([a-zA-Z]{2,10})$/;
            const v = this.form.email || '';
            if (!v) {
                this.$set(this.errors, 'email', 'Required');
                this.emailStatus = '';
                return;
            }
            if (!filter.test(v)) {
                this.$set(this.errors, 'email', 'That doesn\'t look like a valid email');
                this.emailStatus = '';
                return;
            }
            this.$delete(this.errors, 'email');
            // #1459 — this is a PUBLIC, unauthenticated form. The old
            // `user/emailExists` dup-check sits behind `auth:api`, so for an
            // anonymous worker it 401s and api.js's global interceptor bounces
            // them to /#/login (observed mid-form at the address step). Email
            // format validation above is sufficient; the backend already links
            // an existing user by phone in ensureStubEmployee. No server call.
            this.emailStatus = '';
        },
        handlePhoto(e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = ''; // let the same file be re-picked
            if (!file) return;
            this.photoError = '';
            if (!/^image\//.test(file.type)) {
                this.photoError = 'Please choose an image file.';
                return;
            }
            this.photoUploading = true;
            this.photoUploaded = false;
            this.resizeImage(file)
                .then((blob) => {
                    if (this.photoPreview) URL.revokeObjectURL(this.photoPreview);
                    this.photoPreview = URL.createObjectURL(blob);
                    return this.uploadPhoto(blob);
                })
                .then(() => { this.photoUploaded = true; })
                .catch((err) => {
                    const msg = err && err.response && err.response.data && err.response.data.status_message;
                    this.photoError = msg === 'photo_not_accepted'
                        ? 'This onboarding link does not accept a photo.'
                        : 'Could not upload the photo. Please try again.';
                })
                .finally(() => { this.photoUploading = false; });
        },
        // Downscale to <=1600px on the longest edge before upload so a
        // phone photo does not push several megabytes over a cell link.
        resizeImage(file) {
            const MAX = 1600;
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    URL.revokeObjectURL(url);
                    let w = img.naturalWidth;
                    let h = img.naturalHeight;
                    if (w > MAX || h > MAX) {
                        const scale = Math.min(MAX / w, MAX / h);
                        w = Math.round(w * scale);
                        h = Math.round(h * scale);
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                    canvas.toBlob(
                        (blob) => { blob ? resolve(blob) : reject(new Error('encode_failed')); },
                        'image/jpeg',
                        0.85
                    );
                };
                img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode_failed')); };
                img.src = url;
            });
        },
        uploadPhoto(blob) {
            const token = this.$route.params.token;
            const fd = new FormData();
            fd.append('photo', blob, 'onboarding-photo.jpg');
            return service.postUrl('/api/v3/onboarding/intake/' + token + '/photo', fd);
        },
        submit() {
            this.validateField('first_name');
            this.validateField('last_name');
            this.checkEmailExists();
            // Validate the cell when it's required, or when the worker chose to
            // enter an optional one (so a typo still gets caught).
            if (this.cellRequired || this.form.cell) this.validateCell();
            // #1827 — catch a missing/short code here rather than spending a
            // throttled submit on it. The server checks it regardless.
            if (this.needsSmsCode) {
                if (!/^\d{6}$/.test(this.form.sms_code || '')) {
                    this.$set(this.errors, 'sms_code', 'Enter the 6-digit code we texted you.');
                    return;
                }
                this.$delete(this.errors, 'sms_code');
            }
            if (!this.canSubmit) return;

            // #1720 P1 — if they entered an address, confirm its location on a map
            // (verify + move pin) before submitting. Confirming sets coords_verified
            // and continues to doSubmit(); no address entered -> submit straight through.
            if (this.hasEnteredAddress && !this.form.coords_verified) {
                this.pinDraggable = false;
                this.showVerify = true;
                return;
            }
            this.doSubmit();
        },
        doSubmit() {
            this.submitting = true;
            this.submitError = '';
            const payload = {
                first_name: this.form.first_name,
                last_name: this.form.last_name,
                email: this.form.email,
                street: this.form.street,
                pobox: this.form.pobox,
                city: this.form.city,
                state: this.form.state,
                postal_code: this.form.postal_code,
                country: this.form.country,
                lat: this.form.lat,
                lng: this.form.lng,
                coords_source: this.form.coords_source,
                coords_verified: this.form.coords_verified,
                emergency: {
                    name: this.form.emergency_name,
                    relationship: this.form.emergency_relationship,
                    phone: this.form.emergency_phone,
                },
            };

            if (this.isCodeMode) {
                // One-form: include phone, honeypot, and the time-trap nonce.
                // Backend creates the stub (preset applied) + emails a verify
                // link; the worker becomes 'basic' only after clicking it.
                payload.phone = this.form.cell;
                payload.company_url = this.companyUrl; // honeypot (stays empty)
                payload.nonce = this.nonce;
                if (this.needsSmsCode) payload.sms_code = this.form.sms_code; // #1827
                const code = this.$route.params.code;
                service.postUrl('/api/v3/onboarding/intake/code/' + code + '/submit', payload, {})
                    .then(() => { this.verifyEmailSent = true; })
                    .catch((err) => {
                        const msg = err && err.response && err.response.data && err.response.data.status_message;
                        if (msg === 'email_in_client' || msg === 'email_in_database') {
                            this.emailConflictSubdomain = (err.response.data.subdomain) || '';
                            this.emailConflict = msg; // open the email-conflict modal
                            return;
                        }
                        // #1827 — a gate refusal on submit. Report it against
                        // the code field when the code is the thing that's
                        // wrong, so the worker sees it where they can fix it.
                        if (msg === 'qr_sms_code_invalid' || msg === 'qr_sms_verification_required') {
                            this.$set(this.errors, 'sms_code', msg === 'qr_sms_code_invalid'
                                ? 'That code is not right, or it has expired. Tap "Resend" for a new one.'
                                : 'Please enter the code we texted you.');
                            return;
                        }
                        const status = err && err.response ? err.response.status : 0;
                        this.submitError = (msg === 'too_fast')
                            ? 'That was a little too quick — please take a moment and try again.'
                            : (msg === 'too_many_requests' || status === 429)
                                ? 'Too many attempts from this connection. Please wait a few minutes and try again.'
                            : (msg === 'invalid_phone')
                                ? 'Please enter a valid mobile number.'
                                : (msg === 'qr_daily_cap_reached')
                                    ? "This sign-up link has reached its limit for today. Please try again tomorrow, or ask your steward to sign you up directly."
                                    : (msg === 'qr_disabled' || msg === 'qr_window_closed')
                                        ? 'This link is not accepting new sign-ups right now. Please check with your steward.'
                                        : (msg || 'Something went wrong. Please try again, or contact your steward.');
                    })
                    .finally(() => { this.submitting = false; });
                return;
            }

            // Classic per-stub token flow: promote stub → basic + dual-channel reset.
            // Email-only links carry no phone on the stub, so the worker types
            // their cell here — send it so the backend persists it and the SMS
            // password-setup link can fire.
            if (this.form.cell) payload.phone = this.form.cell;
            const token = this.$route.params.token;
            service.postUrl('/api/v3/onboarding/intake/' + token + '/submit', payload, {})
                .then(() => {
                    this.submitted = true;
                })
                .catch((err) => {
                    const msg = err && err.response && err.response.data && err.response.data.status_message;
                    if (msg === 'email_in_client' || msg === 'email_in_database') {
                        this.emailConflictSubdomain = (err.response.data.subdomain) || '';
                        this.emailConflict = msg; // open the email-conflict modal
                        return;
                    }
                    this.submitError = (msg === 'invalid_phone')
                        ? 'Please enter a valid mobile number.'
                        : (msg || 'Something went wrong. Please try again, or contact your steward.');
                })
                .finally(() => { this.submitting = false; });
        },
        // #1720 P1 — pin-confirm overlay: track coords, move pin, confirm, cancel.
        onCoords({ lat, lng, source }) {
            this.form.lat = lat;
            this.form.lng = lng;
            this.form.coords_source = source;
        },
        enableMovePin() {
            this.pinDraggable = true;
        },
        // "That's correct" / "Save this location" — a human has confirmed the pin,
        // so mark it verified and continue the submit.
        confirmPin() {
            this.form.coords_verified = true;
            this.showVerify = false;
            this.pinDraggable = false;
            this.doSubmit();
        },
        closeMap() {
            this.showVerify = false;
            this.pinDraggable = false;
        },
        // #1459 — email-conflict modal actions.
        closeEmailConflict() {
            this.emailConflict = null;
            this.$nextTick(() => {
                const el = this.$refs.emailInput;
                if (el && el.focus) el.focus();
            });
        },
        emailConflictSignIn() {
            this.emailConflict = null;
            this.$router.push('/login');
        },
        // #1459 mobile — main.js's router guard forces the viewport to
        // width=1024 on mobile devices (so the desktop admin app renders).
        // The public onboarding pages are mobile-first and responsive, so
        // override back to device-width — otherwise a phone lays the form out
        // at 1024px and scales it to ~38% ("appeared small"). Same fix as
        // loginPage.vue.
        forceResponsiveViewport() {
            const content = 'width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=no';
            const existing = document.querySelector('meta[name="viewport"]');
            const meta = document.createElement('meta');
            meta.setAttribute('name', 'viewport');
            meta.setAttribute('content', content);
            if (existing) {
                existing.parentNode.replaceChild(meta, existing);
            } else {
                document.head.appendChild(meta);
            }
        },
    },
    mounted() {
        this.forceResponsiveViewport();
        if (this.mode === 'verify') {
            this.runVerify();
        } else if (this.mode === 'code') {
            this.loadByCode();
        } else {
            this.loadIntake();
        }
    },
};
</script>

<style scoped>
/* Mirrors resources/views/auth/reset-password-universal.blade.php:
   dark blue field, dark header bar, translucent glass card, white text,
   56px inputs, mobile @media at 600px that scales everything up. */

.onboardFormVue {
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background: #335479;
    color: #fff;
    display: flex;
    flex-direction: column;
    -webkit-text-size-adjust: 100%;
}

/* ── Client brand header (top) ── */
.onboard-brand-header {
    background: #111E2C;
    text-align: center;
    padding: 18px 16px;
    flex-shrink: 0;
}
.onboard-brand-logo {
    max-height: 64px;
    max-width: 80%;
    object-fit: contain;
}
.onboard-brand-name {
    color: #fff;
    font-size: 20px;
    font-weight: 600;
}

/* #1459 honeypot — visually + a11y hidden, but present in the DOM so bots
   that auto-fill every field trip it. Not display:none (some bots skip those). */
.onboard-hp {
    position: absolute !important;
    left: -10000px !important;
    top: auto !important;
    width: 1px !important;
    height: 1px !important;
    overflow: hidden !important;
}

/* ── Main area ── */
.onboard-main {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 32px 24px;
}

/* ── Glass card ── */
.onboard-card {
    width: 100%;
    max-width: 480px;
    background: rgba(255,255,255,0.08);
    border-radius: 16px;
    padding: 40px 32px 32px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
}

.onboard-header { text-align: center; margin-bottom: 24px; }
.onboard-title {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    color: #fff;
}
.onboard-subtitle {
    margin: 8px 0 0;
    font-size: 15px;
    color: rgba(255,255,255,0.7);
}

.onboard-note {
    background: rgba(255,184,77,0.12);
    border-left: 3px solid #f0a800;
    padding: 12px 14px;
    border-radius: 6px;
    margin: 0 0 24px;
    color: #ffe2a8;
    font-size: 14px;
    line-height: 1.4;
}

/* ── Form fields ── */
.form-group { margin-bottom: 20px; }
.form-group label {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: rgba(255,255,255,0.9);
    margin-bottom: 8px;
}

.onboard-row {
    display: flex;
    gap: 12px;
}
.onboard-col-2 { flex: 1; }

.onboard-input {
    width: 100%;
    box-sizing: border-box;
    height: 56px;
    padding: 0 18px;
    font-size: 17px;
    border: 2px solid rgba(255,255,255,0.15);
    border-radius: 10px;
    outline: none;
    background: rgba(255,255,255,0.08);
    color: #fff;
    transition: border-color 0.2s, background 0.2s;
    -webkit-appearance: none;
}
.onboard-input::placeholder { color: rgba(255,255,255,0.35); }
.onboard-input:focus {
    border-color: rgba(255,255,255,0.45);
    background: rgba(255,255,255,0.14);
}
.onboard-input.is-invalid {
    border-color: #ef9a9a;
    background: rgba(244,67,54,0.18);
}
.onboard-input--readonly {
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.7);
}

.onboard-req { color: #ef9a9a; }
.onboard-optional {
    color: rgba(255,255,255,0.55);
    font-weight: normal;
    font-size: 0.85em;
}
.onboard-help {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    color: rgba(255,255,255,0.55);
}
.onboard-warn {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    color: #ffe2a8;
}
/* #1827 — code field + "text me a code" on one row. Wraps on narrow phones
   rather than squeezing the 6-digit field, which is the one that must stay
   comfortably tappable. */
.onboard-sms-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: stretch;
}
.onboard-sms-row .onboard-input {
    flex: 1 1 140px;
    min-width: 0;
    letter-spacing: 2px;
}
.onboard-sms-send {
    flex: 0 0 auto;
    white-space: nowrap;
}
.onboard-error {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    color: #ef9a9a;
}
.onboard-error--global {
    text-align: center;
    margin-top: 20px;
    padding: 14px 16px;
    background: rgba(244,67,54,0.25);
    color: #ef9a9a;
    border-radius: 10px;
    font-size: 15px;
}

.onboard-section-h {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 28px 0 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255,255,255,0.12);
    color: rgba(255,255,255,0.65);
    font-weight: 600;
}

.onboard-submit {
    display: block;
    width: 100%;
    height: 56px;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    background: #4CAF50;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    margin-top: 28px;
    transition: background 0.2s, transform 0.1s;
    -webkit-tap-highlight-color: transparent;
}
.onboard-submit:hover { background: #43A047; }
.onboard-submit:active:not(:disabled) {
    transform: scale(0.98);
    background: #388E3C;
}
.onboard-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

/* ── Success / done state ── */
.onboard-success {
    text-align: center;
    padding: 12px 0 8px;
}
.onboard-success .fa { font-size: 64px; color: #4CAF50; margin-bottom: 16px; }
.onboard-success h2 { font-size: 22px; font-weight: 700; margin: 0 0 8px; color: #fff; }
.onboard-success p {
    font-size: 15px;
    color: rgba(255,255,255,0.7);
    margin: 0;
    line-height: 1.5;
}

/* ── Photo capture ── */
.onboard-photo-instructions { margin: 0 0 12px; }
.onboard-photo-preview { text-align: center; margin-bottom: 12px; }
.onboard-photo-preview img {
    width: 140px;
    height: 140px;
    object-fit: cover;
    border-radius: 10px;
    border: 2px solid rgba(255,255,255,0.25);
}
.onboard-photo-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    padding: 14px;
    border: 2px dashed rgba(255,255,255,0.35);
    border-radius: 10px;
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.85);
    font-weight: 600;
    cursor: pointer;
}
.onboard-photo-btn:active { background: rgba(255,255,255,0.12); }
.onboard-photo-input { display: none; }

/* ── CS footer (bottom) ── */
.onboard-footer {
    text-align: center;
    padding: 20px 0 28px;
    flex-shrink: 0;
}
.onboard-footer-logo {
    height: 28px;
    opacity: 0.55;
    transition: opacity 0.2s;
}
.onboard-footer a:hover .onboard-footer-logo { opacity: 0.85; }

/* ── #1459 email-conflict modal (glass card over a dimmed form) ── */
.onboard-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(20, 24, 31, 0.55);
    backdrop-filter: blur(2px);
}
.onboard-modal {
    width: 100%;
    max-width: 420px;
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
    padding: 24px 22px 20px;
    text-align: center;
}
.onboard-modal-title {
    margin: 0 0 10px;
    font-size: 20px;
    font-weight: 700;
    color: #1f2733;
}
.onboard-modal-body {
    margin: 0 0 20px;
    font-size: 15px;
    line-height: 1.45;
    color: #4a5562;
}
.onboard-modal-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.onboard-modal-btn {
    height: 48px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
}
.onboard-modal-btn--primary {
    background: #2a6ebd;
    color: #fff;
}
.onboard-modal-btn--primary:hover { background: #245fa3; }
.onboard-modal-btn--ghost {
    background: #fff;
    color: #4a5562;
    border-color: #d7dde5;
}
.onboard-modal-btn--ghost:hover { background: #f3f5f8; }

/* ── Mobile: make everything BIG ── */
@media (max-width: 600px) {
    .onboard-main { padding: 20px 16px; }
    .onboard-card {
        padding: 28px 20px 24px;
        border-radius: 12px;
    }
    .onboard-title { font-size: 22px; }
    .form-group label { font-size: 16px; }
    .onboard-input {
        height: 60px;
        font-size: 18px;
        border-radius: 12px;
    }
    .onboard-submit {
        height: 60px;
        font-size: 19px;
        border-radius: 12px;
        margin-top: 24px;
    }
    .onboard-row { gap: 10px; }
    .onboard-error--global { font-size: 16px; padding: 16px; }
    .onboard-brand-logo { max-height: 56px; }
    .onboard-footer-logo { height: 24px; }
    .onboard-modal-title { font-size: 22px; }
    .onboard-modal-body { font-size: 16px; }
    .onboard-modal-btn { height: 56px; font-size: 18px; }
}
</style>
